// 활동 이력 타임라인 병합 — 소스가 셋이라 순서 규칙을 한 곳에 모은다(순수 함수).
//
//   ① 자동 마일스톤(cic 집계 파생 · 연도 있는 것만)
//   ② 레거시 수동(cic creator_dashboard_profile.notable_history) — 읽기 전용
//   ③ 포털 수동 항목(우리 Mongo) — 편집 가능
//
// 정렬: **연도 내림차순 → 같은 해면 자동 우선 → sortOrder 오름차순**.
//   자동을 앞에 두는 이유: 자동 마일스톤은 "그 해에 실제로 달성된 사실"이고 수동은 그 해에 대한
//   사람의 기록이라, 같은 해에서 사실을 먼저 보여주는 편이 읽기 순서로 자연스럽다.
import type { NotableHistoryEntry } from '../../notableHistoryApi'
import type { Platform, ProfileData } from '../../creatorsApi'

type History = ProfileData['history']

/** `brand_count` 라벨은 BE 에서 `"누적 협업 브랜드 " + step + "+"` 로 만든다(채널당 최고 계단 1개). */
const BRAND_COUNT_STEP = /(\d+)\s*\+\s*$/

/** 합본에서만 붙는 출처. 채널마다 갈리는 계단(`brand_count`)에만 채워지고 나머지는 undefined. */
export type MergedMilestone = History['milestones'][number] & { sourcePlatform?: Platform }
export interface MergedHistory {
  milestones: MergedMilestone[]
  manual: History['manual']
}

/**
 * 세 채널 조각의 활동 이력을 **하나로 합친다**.
 *
 * 활동 이력은 채널 단위 사실이 아니라 **크리에이터 단위 커리어**라, 옆 블록의 협업 브랜드 탭을
 * 눌렀다고 바뀌면 안 된다(눌러서 바뀌는 순간 화면이 "이 사람의 이력"이 아니라 "이 채널의 이력"을
 * 주장하게 된다). 그래서 탭을 따르지 않고 전 채널을 합쳐 한 벌로 그린다.
 *
 * 마일스톤 대부분(`sub_*` · `top_view_*` · `branded_pctile` · `collab_years`)은 creatorId·channels
 * 파생이라 세 조각에 **같은 값이 그대로 중복**된다 → `code|year|label` 로 dedupe.
 *
 * 예외가 `brand_count` 하나다. 이건 그 채널의 협업 브랜드 수 파생이라 채널마다 계단이 갈린다
 * (YouTube 30+ / Instagram 10+). 이건 **채널마다 한 줄씩 남긴다** — 각 채널에서 실제로 달성한
 * 서로 다른 성과이지 같은 값의 중복이 아니다. 대신 줄마다 `sourcePlatform` 을 실어, 화면이
 * "어느 채널의 성과인지"를 밝힐 수 있게 한다(출처 없이 합치면 30+ 가 어느 채널인지 알 수 없다).
 * 라벨 형식이 바뀌어 숫자를 못 읽으면 일반 dedupe 경로로 보낸다 — 값이 사라지는 것보다 낫다.
 */
export function mergeHistoryAcrossChannels(
  sources: Array<{ platform?: Platform; history: History | null | undefined }>,
): MergedHistory {
  const milestones: MergedMilestone[] = []
  // 채널별로 여러 줄이 되는 계단은 따로 모은다 — 뒤에서 계단 큰 순으로 세워 붙인다.
  const brandCounts: Array<{ step: number; milestone: MergedMilestone }> = []
  const seen = new Set<string>()

  for (const { platform, history } of sources) {
    if (!history) continue
    for (const milestone of history.milestones) {
      const step =
        milestone.code === 'brand_count'
          ? Number(BRAND_COUNT_STEP.exec(milestone.label)?.[1])
          : NaN
      const splitByChannel = Number.isFinite(step)
      // 채널이 갈리는 계단은 채널까지 key 에 넣는다 — 안 넣으면 두 번째 채널 것이 중복으로 잘린다.
      const key = splitByChannel
        ? `brand_count|${platform ?? ''}`
        : `${milestone.code}|${milestone.year ?? ''}|${milestone.label}`
      if (seen.has(key)) continue
      seen.add(key)
      if (splitByChannel) brandCounts.push({ step, milestone: { ...milestone, sourcePlatform: platform } })
      else milestones.push(milestone)
    }
  }
  // 계단 내림차순(성과 큰 순). 같은 계단이면 sources 순서 = 채널 순서를 유지한다(안정 정렬).
  brandCounts.sort((a, b) => b.step - a.step)
  milestones.push(...brandCounts.map((b) => b.milestone))

  const manual: History['manual'] = []
  const seenManual = new Set<string>()
  for (const { history } of sources) {
    if (!history) continue
    for (const entry of history.manual) {
      const key = `${entry.year ?? ''}|${entry.note}`
      if (seenManual.has(key)) continue
      seenManual.add(key)
      manual.push(entry)
    }
  }

  return { milestones, manual }
}

export type HistoryRowSource = 'auto' | 'legacy' | 'manual'

export interface HistoryRow {
  key: string
  year: number
  text: string
  source: HistoryRowSource
  /** 포털 수동 항목만 보유 — 편집·삭제 대상 식별자. */
  entryId?: string
  sortOrder: number
}

/** "2024" 같은 문자열 연도를 숫자로. 파싱 불가면 null(정렬 불가라 타임라인에서 제외). */
function toYear(v: string | undefined): number | null {
  const n = Number(String(v ?? '').trim())
  return Number.isInteger(n) && n > 0 ? n : null
}

export function mergeHistoryRows(
  history: ProfileData['history'],
  entries: NotableHistoryEntry[] = [],
): HistoryRow[] {
  const rows: HistoryRow[] = []

  history.milestones.forEach((m, i) => {
    const year = toYear(m.year)
    if (year == null) return // 연도 없는 마일스톤은 상단 배지 그룹 담당 — 타임라인에 넣지 않는다
    rows.push({ key: `ms-${m.code}-${i}`, year, text: m.label, source: 'auto', sortOrder: 0 })
  })
  history.manual.forEach((m, i) => {
    const year = toYear(m.year)
    if (year == null) return
    rows.push({ key: `lg-${i}`, year, text: m.note, source: 'legacy', sortOrder: 0 })
  })
  entries.forEach((e) => {
    rows.push({
      key: `pt-${e.id}`,
      year: e.year,
      text: e.note,
      source: 'manual',
      entryId: e.id,
      sortOrder: e.sortOrder,
    })
  })

  const rank = (s: HistoryRowSource): number => (s === 'auto' ? 0 : 1)
  return rows.sort(
    (a, b) => b.year - a.year || rank(a.source) - rank(b.source) || a.sortOrder - b.sortOrder,
  )
}
